// Veranstaltungen unterwegs (docs/veranstaltungen/SPEZIFIKATION.md §8, M4): Übergabe und Abholung durch Fahrer – in der
// Fahrer-Ansicht von Ordaneo (`sales.event.driverStops`, `sales.event.pickupForm`, `sales.event.handover`, Abholung mit
// `sales.event.recordPickup`) und über die Bestell-App (`sales.channel.submitEventInquiry`, `sales.channel.reportEventDelivery`,
// `sales.channel.reportEventPickup`; Vertrag in docs/integration/bestell-app.md „Veranstaltungen“).
//
// Ein Stopp einer Tour ist entweder die Lieferung (Auftrag der Veranstaltung) oder die Abholung (Abholauftrag AH-…, entsteht mit
// der Übergabe). Mengen in Tausendstel des Gebindes; Leihartikel Stück × 1000.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { EVENT_STATUSES } from '../lib/events'
import { STOP_STATUSES } from '../lib/labels'
import { PROOF_BODY_BYTES } from './afterDelivery'
import { zDay, zEmptiesEntry, zTime } from './common'
import { zEventProof, zEventRentalReturn, zEventReturnLine, zEventView } from './event'

const FEATURE = 'events'
const CREATE = 'sales.event.create'
const REPORT = 'sales.event.report'

const zText = (max: number) => z.string().trim().max(max)
const zInstant = z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' })
const zReportId = zText(80).min(8, { error: 'Die Kennung der Meldung ist zu kurz.' })

/** Was bei der Abholung zu zählen ist – Grundlage für das Formular des Fahrers und das Abbild für die Bestell-App. */
export const zEventPickupForm = z.object({
  eventId: zId,
  number: z.string(),
  title: z.string(),
  status: z.enum(EVENT_STATUSES),
  statusLabel: z.string(),
  customerName: z.string(),
  contact: z.object({ name: z.string().nullable(), phone: z.string().nullable() }),
  venueText: z.string().nullable(),
  venueNote: z.string().nullable(),
  /** Positionen der Kommissionslieferung: noch beim Kunden (`qty`) und der Vorschlag der vollen Rückware */
  lines: z.array(z.object({ lineId: zId, sku: z.string().nullable(), name: z.string(), unit: z.string().nullable(), unitCode: z.string().nullable(), qty: z.int(), returnedQty: z.int() })),
  /** Pfandarten der gelieferten Gebinde (Leergut in Stück × 1000) mit Vorschlag */
  depositTypes: z.array(z.object({ depositTypeId: zId, name: z.string(), emptiesQty: z.int() })),
  /** ausgegebene Leihartikel je Buchung mit Vorschlag (zurück, beschädigt) */
  rentals: z.array(z.object({ bookingId: zId, sku: z.string(), name: z.string(), unit: z.string(), qty: z.int(), serialTracked: z.boolean(), serialNos: z.array(z.string()), returnedQty: z.int(), damagedQty: z.int() })),
  /** schon erfasst (wird mit einer neuen Erfassung ersetzt) */
  recorded: z.boolean(),
})

export const eventPickupForm = defineContract({
  name: 'sales.event.pickupForm',
  title: 'Abholung einer Veranstaltung vorbereiten',
  description: 'Was bei der Abholung zu zählen ist: Ware beim Kunden, Leergut je Pfandart, ausgegebene Leihartikel – ohne Preise.',
  llm: {
    description:
      'Liefert für eine gelieferte Veranstaltung, was der Fahrer bei der Abholung zählt: je Position der Kommissionslieferung die Menge ' +
      'beim Kunden, die Pfandarten fürs Leergut und die ausgegebenen Leihartikel – mit dem Vorschlag aus einer früheren Erfassung. ' +
      'Ohne Preise. Erfasst wird mit sales.event.recordPickup.',
    keywords: ['Abholung', 'Rückware zählen', 'Leergut', 'Leihartikel zurück'],
  },
  input: z.strictObject({ id: zId }),
  output: zEventPickupForm,
  permission: REPORT,
  feature: FEATURE,
  risk: 'read',
  scope: 'record',
})

export const zDriverStop = z.object({
  /** Stopp auf einer Tour; null = die Veranstaltung ist an diesem Tag auf keiner Tour */
  stopId: zId.nullable(),
  tourId: zId.nullable(),
  tourName: z.string().nullable(),
  vehicleCode: z.string().nullable(),
  seq: z.int().nullable(),
  stopStatus: z.enum(STOP_STATUSES).nullable(),
  plannedEta: z.date().nullable(),
  kind: z.enum(['delivery', 'pickup']),
  eventId: zId,
  number: z.string(),
  title: z.string(),
  status: z.enum(EVENT_STATUSES),
  statusLabel: z.string(),
  customerName: z.string(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  venueText: z.string().nullable(),
  venueNote: z.string().nullable(),
  /** „08:00–10:00 Uhr“ der Anlieferung bzw. Abholung */
  windowText: z.string().nullable(),
  guests: z.int().nullable(),
  /** Übergabe bestätigen möglich (Ware und Leihartikel sind gebucht, noch nicht übergeben) */
  canHandover: z.boolean(),
  /** Lieferung gebucht? Sonst bucht das Lager sie vor der Fahrt („Lieferung buchen“ ohne Übergabe) */
  shipped: z.boolean(),
  /** Abholung erfassen möglich */
  canPickup: z.boolean(),
})

export const eventDriverStops = defineContract({
  name: 'sales.event.driverStops',
  title: 'Veranstaltungen des Tages für Fahrer',
  description: 'Lieferungen und Abholungen von Veranstaltungen an einem Tag – mit Tour, Stopp und den möglichen Schritten.',
  llm: {
    description:
      'Liefert für einen Tag (Standard heute) die Lieferungen (Anlieferung am Tag) und Abholungen (Abholtag) von Veranstaltungen an ' +
      'den Standorten bzw. Fahrzeugen der Person – mit Tour, Fahrzeug, Stoppnummer und ob Übergabe bzw. Abholung jetzt möglich sind.',
    keywords: ['Fahrer', 'Veranstaltung heute', 'Abholung heute', 'Lieferung Fest'],
  },
  input: z.strictObject({ date: zDay.optional() }),
  output: z.object({ date: z.string(), stops: z.array(zDriverStop) }),
  permission: REPORT,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

export const eventHandover = defineContract({
  name: 'sales.event.handover',
  title: 'Übergabe einer Veranstaltung bestätigen',
  description: 'Der Fahrer bestätigt die Übergabe beim Kunden: „Geliefert“, Stopp erledigt, Abholauftrag für die Disposition angelegt.',
  llm: {
    description:
      'Bestätigt die Übergabe einer zugesagten Veranstaltung, deren Lieferung gebucht ist (Kommissionslieferung bzw. ausgegebene ' +
      'Leihartikel): Zustand „Geliefert“, der Stopp der Tour ist erledigt, und der Abholauftrag (AH-…) ist am Abholtag in der ' +
      'Disposition planbar. Optional Empfänger, Notiz und Unterschrift (PNG/JPEG, höchstens 300 KB). Wiederholt aufgerufen: keine Änderung.',
    keywords: ['Übergabe', 'geliefert', 'Fest beliefert', 'Fahrer'],
  },
  input: z.strictObject({
    id: zId,
    stopId: zId.nullish(),
    receivedBy: zText(120).nullish(),
    note: zText(500).nullish(),
    proof: zEventProof.nullish(),
  }),
  output: zEventView,
  permission: REPORT,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  effects: { reversible: false },
  // Unterschrift und Foto als Base64 (Zustellnachweis, contract/afterDelivery.ts)
  limits: { bodyBytes: PROOF_BODY_BYTES },
})

// ---- Bestell-App (öffentliche API) -------------------------------------------------------------------------------------

/**
 * Veranstaltung an einem Stopp der Touren (sales.channel.tours, nur wenn der Betrieb die Anbindung eingeschaltet hat):
 * `kind` delivery = Ware und Leihartikel übergeben (→ sales.channel.reportEventDelivery), pickup = zählen und zurücknehmen
 * (→ sales.channel.reportEventPickup, mit `pickup`).
 */
export const zChannelEventStop = z.object({
  eventId: zId,
  number: z.string(),
  title: z.string(),
  kind: z.enum(['delivery', 'pickup']),
  status: z.enum(EVENT_STATUSES),
  guests: z.int().nullable(),
  contact: z.object({ name: z.string().nullable(), phone: z.string().nullable() }),
  venueNote: z.string().nullable(),
  /** nur bei der Abholung: Ware beim Kunden, Pfandarten und Leihartikel mit ihren Kennungen für die Meldung */
  pickup: zEventPickupForm.pick({ lines: true, depositTypes: true, rentals: true }).nullable(),
})

const zEventReply = z.object({
  eventId: zId,
  number: z.string(),
  status: z.enum(EVENT_STATUSES),
  statusLabel: z.string(),
  /** true: Diese Meldung lag schon vor – es wurde nichts geändert */
  replayed: z.boolean(),
  message: z.string(),
})

export const channelSubmitEventInquiry = defineContract({
  name: 'sales.channel.submitEventInquiry',
  title: 'Veranstaltungsanfrage eines Vertriebskanals übergeben',
  description: 'Legt aus einer Anfrage der Bestell-App eine Veranstaltung im Zustand „Anfrage“ an (Quelle App) – mit Prüfung der Leihartikel.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Legt eine Veranstaltung im Zustand „Anfrage“ an (Quelle „app“, Verweis = ' +
      '`inquiryId`): Kunde über `customerNumber` oder Interessent (`contact`), Ort, Termine, Gäste, Getränke über Artikelnummer und ' +
      'Einheitencode (Menge in Tausendstel des Gebindes), Leihartikel über Artikelnummer und Stückzahl. Antwortet mit Nummer und der ' +
      'Verfügbarkeit je Leihartikel im Zeitraum (verfügbar, knapp, nicht verfügbar). Dieselbe `inquiryId` legt nichts Neues an ' +
      '(`replayed: true`). Reserviert wird nichts – Angebot und Zusage macht der Vertrieb.',
    keywords: ['Veranstaltungsanfrage', 'Festanfrage', 'Bestell-App', 'Schnittstelle'],
  },
  input: z
    .strictObject({
      /** Kennung der Anfrage in der App – Schlüssel der Idempotenz */
      inquiryId: zText(80).min(1, { error: 'Die Kennung der Anfrage fehlt.' }),
      customerNumber: zText(40).optional(),
      contact: z
        .strictObject({
          name: zText(200).min(1, { error: 'Der Name fehlt.' }),
          company: zText(200).optional(),
          email: z.email({ error: 'Die E-Mail-Adresse ist ungültig.' }).optional(),
          phone: zText(60).optional(),
        })
        .optional(),
      /** Code des liefernden Standorts ('ZL'); ohne Angabe der Standard der Veranstaltungen */
      locationCode: zText(20).optional(),
      title: zText(200).min(1, { error: 'Der Titel fehlt.' }),
      venue: z.strictObject({ street: zText(200).optional(), zip: zText(20).optional(), city: zText(120).optional(), note: zText(500).optional() }).optional(),
      eventStart: zDay.optional(),
      eventEnd: zDay.optional(),
      eventStartTime: zTime.optional(),
      eventEndTime: zTime.optional(),
      deliveryDate: zDay.optional(),
      deliveryTimeFrom: zTime.optional(),
      deliveryTimeTo: zTime.optional(),
      pickupDate: zDay.optional(),
      pickupTimeFrom: zTime.optional(),
      pickupTimeTo: zTime.optional(),
      guests: z.int().min(1).max(100_000).optional(),
      note: zText(2000).optional(),
      /** Getränke und Ware: Artikelnummer, Einheitencode (ohne: Verkaufsgebinde), Menge in Tausendstel (3 Kästen = 3000) */
      lines: z
        .array(z.strictObject({ sku: zText(60).min(1, { error: 'Die Artikelnummer fehlt.' }), unit: zText(40).optional(), orderQty: z.int().min(1).max(1_000_000_000), note: zText(200).optional() }))
        .max(100)
        .default([]),
      /** Leihartikel: Artikelnummer und Stückzahl */
      rentals: z.array(z.strictObject({ sku: zText(60).min(1, { error: 'Die Artikelnummer fehlt.' }), qty: z.int().min(1).max(9999), note: zText(200).optional() })).max(50).default([]),
    })
    .refine((input) => input.customerNumber !== undefined || input.contact !== undefined, { error: 'Bitte Kundennummer oder Ansprechpartner angeben.', path: ['contact'] }),
  output: zEventReply.extend({
    /** Verfügbarkeit je Leihartikel im Zeitraum (ohne Termine: open) */
    rentals: z.array(z.object({ sku: z.string(), qty: z.int(), status: z.enum(['available', 'tight', 'unavailable', 'open']), statusLabel: z.string(), freeQty: z.int(), message: z.string().nullable() })),
  }),
  permission: CREATE,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  publicApi: true,
  limits: { maxItems: 150, itemCount: (input) => input.lines.length + input.rentals.length },
})

export const channelReportEventDelivery = defineContract({
  name: 'sales.channel.reportEventDelivery',
  title: 'Übergabe einer Veranstaltung melden',
  description: 'Die Fahrer-App meldet die Übergabe beim Kunden – wie „Übergabe bestätigen“ in Ordaneo.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet die Übergabe einer Veranstaltung (Stopp der Art Lieferung): „Geliefert“, ' +
      'Stopp erledigt, Abholauftrag angelegt. Voraussetzung: Die Lieferung ist in Ordaneo gebucht. Idempotent über `reportId`.',
    keywords: ['Übergabe melden', 'Veranstaltung', 'Fahrer-App', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    reportId: zReportId,
    eventId: zId,
    stopId: zId.optional(),
    at: zInstant,
    receivedBy: zText(120).optional(),
    driverName: zText(120).optional(),
    note: zText(500).optional(),
    proof: zEventProof.optional(),
  }),
  output: zEventReply,
  permission: REPORT,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  publicApi: true,
  effects: { reversible: false },
  limits: { bodyBytes: PROOF_BODY_BYTES },
})

export const channelReportEventPickup = defineContract({
  name: 'sales.channel.reportEventPickup',
  title: 'Abholung einer Veranstaltung melden',
  description: 'Die Fahrer-App meldet, was bei der Abholung zurückkommt: volle Rückware, Leergut, Leihartikel – optional mit Unterschrift.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet die Abholung einer gelieferten Veranstaltung: volle Rückware je Position ' +
      '(`lines`, Kennungen aus dem Abbild am Stopp), Leergut je Pfandart (Stück × 1000), Leihartikel je Buchung (zurück, davon ' +
      'beschädigt; der Rest fehlt), Notiz und Unterschrift (`proof.signature`: PNG oder JPEG als Base64, höchstens 300 KB). Gebucht ' +
      'wird nichts – die Abrechnung macht das Büro. Eine spätere Meldung ersetzt die frühere; dieselbe `reportId` ändert nichts.',
    keywords: ['Abholung melden', 'Rückware', 'Leergut', 'Leihartikel zurück', 'Fahrer-App', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    reportId: zReportId,
    eventId: zId,
    stopId: zId.optional(),
    at: zInstant,
    lines: z.array(zEventReturnLine).max(500).default([]),
    empties: z.array(zEmptiesEntry).max(50).default([]),
    rentals: z.array(zEventRentalReturn).max(100).default([]),
    driverName: zText(120).optional(),
    note: zText(500).optional(),
    proof: zEventProof.optional(),
  }),
  output: zEventReply.extend({ proofStored: z.boolean() }),
  permission: REPORT,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  publicApi: true,
  effects: { reversible: false },
  limits: { maxItems: 651, itemCount: (input) => input.lines.length + input.empties.length + input.rentals.length + 1, bodyBytes: PROOF_BODY_BYTES },
})
